import { useTranslate } from '@festina-life/ui/hooks'

import { useVariantAction } from '../../hooks/useVariantAction'
import { useHook } from '../../lenteHooks'
import { CommandModalButton } from '../commandActions/CommandModalButton'
import type { CommitmentActionButtonProps } from '../commandActions/types'
import { ChangeCommitmentForm } from './ChangeCommitmentForm'

/**
***Change Commitment and State** is the business term used for the command **General Change Commitment and State Command**.

This is a command targeted for expert users only. Be aware that understanding the data model and configuration is a requirement for using this command. Usual validations and help are not available. This command can change everything on a commitment, policies and covers. Use with outmost caution!

## Understanding the Command
Before using this command, it is highly recommended to review the related documentation.
These resources provide important insights into:

- **Purpose & Functionality** of the command
- **Conditions & Business Logic** that apply
- **API Descriptions**, including framework details
- **Configuration Settings**, such as validations (available in the configuration portal)

@links
- [Command Documentation](@root/life/life-server/doc/solution/commands/ChangeCommitmentAndStateCommand.md)

## Change commitment and state in the Portal
This command allows you to change almost everything on a commitment, policies and covers without frames and validations provided the necessary conditions are met.

### How It Works
- This is a **standard command** requiring input fields to be completed before execution.
- **Simulation is not available** for this command.

### What Happens After Applying the Command?
Once executed, you will see the following updates:

✔ **Potentially all information could be updated** on a commitment. <br>
✔ **The command step appears** in the event list (audit trail). <br>
✔ **The commitment status** can be changed to whatever was given as input to the command. <br>
 */
export function ChangeCommitmentCommand(props: CommitmentActionButtonProps) {
  const translate = useTranslate()

  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()

  const actions = useHook('mutators.life.useChangeCommitmentActions')({
    tenantId,
    ownerId,
    ...props,
  })

  const submit = useVariantAction({ ...props, actions })

  return (
    <CommandModalButton
      actionId="life.ChangeCommitmentAndStateCommandData"
      title={translate('changeCommitmentModalButton.title')}
      commandDataForm={ChangeCommitmentForm}
      {...props}
      submit={submit}
    />
  )
}
